import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, TrendingUp, TrendingDown, Repeat } from 'lucide-react';

const FloatingActionButton = ({ onAddCredit, onAddDebit, onAddRecurring }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const menuItems = [
    { 
      icon: TrendingUp, 
      label: 'Entrée', 
      color: 'bg-emerald-500',
      onClick: () => { onAddCredit?.(); setIsOpen(false); }
    },
    { 
      icon: TrendingDown, 
      label: 'Sortie', 
      color: 'bg-rose-400',
      onClick: () => { onAddDebit?.(); setIsOpen(false); }
    },
    { 
      icon: Repeat, 
      label: 'Récurrent', 
      color: 'bg-primary',
      onClick: () => { onAddRecurring?.(); setIsOpen(false); }
    }
  ];

  return (
    <div className="fixed bottom-24 right-5 z-40" data-testid="fab-container">
      {/* Backdrop - Subtle */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/10 backdrop-blur-[2px]"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Menu Items - Premium Style */}
      <AnimatePresence>
        {isOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col-reverse gap-2 items-end">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.label}
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: { delay: index * 0.05, type: 'spring', stiffness: 400, damping: 25 }
                }}
                exit={{ 
                  opacity: 0, 
                  y: 5, 
                  scale: 0.95,
                  transition: { duration: 0.1 }
                }}
                onClick={item.onClick}
                className="flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full bg-white shadow-lg border border-border/50 touch-manipulation active:scale-95 transition-transform"
                data-testid={`fab-${item.label.toLowerCase()}`}
              >
                <div className={`w-7 h-7 rounded-full ${item.color} flex items-center justify-center`}>
                  <item.icon className="h-3.5 w-3.5 text-white" strokeWidth={2} />
                </div>
                <span className="text-sm font-medium text-foreground">{item.label}</span>
              </motion.button>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main FAB Button - Premium */}
      <motion.button
        onClick={toggleMenu}
        className={`w-14 h-14 rounded-full flex items-center justify-center touch-manipulation transition-colors duration-200 ${
          isOpen 
            ? 'bg-foreground' 
            : 'bg-primary'
        }`}
        style={{ boxShadow: isOpen ? '0 10px 30px -5px rgba(0,0,0,0.2)' : 'var(--shadow-primary)' }}
        whileTap={{ scale: 0.95 }}
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        data-testid="fab-main-button"
      >
        {isOpen ? (
          <X className="h-5 w-5 text-white" strokeWidth={2} />
        ) : (
          <Plus className="h-6 w-6 text-white" strokeWidth={2} />
        )}
      </motion.button>
    </div>
  );
};

export default FloatingActionButton;
